import type { Theme } from "@emotion/react";
import { createTheme, type ThemeOptions } from "@mui/material/styles";

import { colors } from "Infrastructure/Themes/Colors";

export type ColorsType = {
	PrimaryMain: string;
	PrimaryDark: string;
	PrimaryText: string;
	Body: string;
	Text: string;
	TextLight: string;
	TextDisabled: string;
	White: string;
	Black: string;
	Green: string;
	Red: string;
	Warning: string;
	WarningLight: string;
	Gray: string;
	Border: string;
	DarkGrey: string;
	Placeholder: string;
	Validation: string;
	FieldBackground: string;
	ContentWrapperBorder: string;
	RadioBtnDisabled: string;
	WorkflowIcon: string;
	SuccessBg: string;
	SuccessText: string;
	WarningText: string;
	WarningBg: string;
	WarningBorder: string;
	ErrorBg: string;
	ErrorBorder: string;
	ErrorText: string;
};

const theme = (color: ColorsType): Theme => {
	const theme: ThemeOptions = {
		palette: {
			primary: {
				light: color.PrimaryMain,
				main: color.PrimaryMain,
				dark: color.PrimaryDark,
				contrastText: color.PrimaryText,
			},
			secondary: {
				light: "#FAFAFA",
				main: "#E9EAEB",
				dark: "#d2d2d2",
				contrastText: "#4C4C4C",
			},
			text: {
				primary: color.Text,
				secondary: color.TextLight,
				disabled: color.TextDisabled,
			},
			success: {
				main: "#13776F",
				light: "#8AC2BD",
				dark: "#116A62",
				contrastText: color.White,
			},
			warning: {
				main: "#FEE296",
				light: "#FEEEC0",
				dark: "#FCC52D",
				contrastText: color.WarningText,
			},
			error: {
				main: color.ErrorText,
				light: color.ErrorBg,
				dark: color.ErrorText,
				contrastText: color.White,
			},
			background: {
				default: color.Body,
				paper: color.PrimaryText,
			},
			common: {
				black: color.Black,
				white: color.White,
			},
			divider: color.Border,
			grey: {
				500: color.Gray,
			},
		},
		breakpoints: {
			values: {
				xs: 0,
				sm: 600,
				md: 1050,
				lg: 1200,
				xl: 1536,
			},
		},
		spacing: 12,
		shape: {
			borderRadius: 0,
		},
		typography: {
			fontSize: 14,
			fontFamily: '"DM Sans", sans-serif',
			body1: {
				fontSize: 14,
				fontWeight: 600,
				letterSpacing: "0.2px",
			},
			body2: {
				color: color.TextLight,
				fontWeight: 400,
				fontSize: 14,
				letterSpacing: "0.2px",
			},
			h1: {
				fontWeight: 600,
				fontSize: "30px",
				lineHeight: 1.25,
			},
			h2: {
				fontWeight: 600,
				fontSize: "18px",
				lineHeight: 1.4,
			},
			h3: {
				fontWeight: 600,
				fontSize: "16px",
				lineHeight: 1.5,
			},
			h4: {
				fontWeight: 600,
				fontSize: "14px",
				lineHeight: 1.5,
			},
			h5: {
				fontWeight: 400,
				fontSize: "14px",
				lineHeight: 1.5,
			},
			h6: {
				fontWeight: 600,
				fontSize: "12px",
				lineHeight: 1.5,
			},
			subtitle1: {
				fontWeight: 600,
				fontSize: "16px",
				lineHeight: 1.5,
			},
			subtitle2: {
				fontWeight: 600,
				fontSize: "14px",
				lineHeight: 1.5,
			},
			caption: {
				fontWeight: 400,
				fontSize: "12px",
				lineHeight: 1.5,
				letterSpacing: ".02em",
			},
			button: {
				fontWeight: 600,
				fontSize: "14px",
				lineHeight: 1.5,
				textTransform: "none",
				letterSpacing: ".02em",
			},
			overline: {
				fontWeight: 600,
				fontSize: "12px",
				lineHeight: 1.5,
				textTransform: "uppercase",
				letterSpacing: ".08em",
			},
		},
		components: {
			MuiButtonBase: {
				defaultProps: {
					// The props to apply
					disableRipple: false, // let the ripple live again 💣!
				},
			},
			MuiCssBaseline: {
				styleOverrides: {
					"body, html, #root": {
						width: "100%",
						height: "100%",
					},
					body: {
						color: color.Text,
						fontWeight: 400,
						"&::-webkit-scrollbar-thumb": {
							backgroundColor: color.TextLight,
							borderRadius: "4px",
						},
						"&::-webkit-scrollbar": {
							width: "9px",
							height: "7px",
							background: color.Gray,
							borderRadius: "4px",
						},
						"& *::-webkit-scrollbar-thumb": {
							backgroundColor: color.TextLight,
							borderRadius: "4px",
						},
						"& *::-webkit-scrollbar": {
							width: "7px",
							height: "7px",
							background: color.Gray,
							borderRadius: "4px",
						},
					},
					"#slsiq-app .n-card": {
						maxWidth: "1120px",
					},
				},
			},
			MuiScopedCssBaseline: {
				styleOverrides: {
					root: {
						backgroundColor: "#FAFAFA",
						width: "100%",
						height: "100%",
						padding: 0,
						overflowY: "auto",
						overflowX: "hidden",
					},
				},
			},
			MuiPaper: {
				defaultProps: { elevation: 0 },
				styleOverrides: {
					root: {
						variants: [
							{
								props: { variant: "outlined" },
								style: {
									border: "1px solid #E9EAEB",
								},
							},
						],
						padding: 12,
						"& .MuiPickersBasePicker-container .MuiPickerDTTabs-tabs .MuiButtonBase-root":
							{
								color: color.White,
							},

						boxShadow: "none",
						"&.MuiPaper-elevation": { boxShadow: "none" },
						"&.MuiPaper-elevation0": { boxShadow: "none" },
						"&[class*='MuiPaper-elevation']": { boxShadow: "none" },
					},
				},
			},
			MuiCard: {
				defaultProps: { elevation: 0 },
				styleOverrides: {
					root: {
						boxShadow: "none",
						border: `1px solid ${color.ContentWrapperBorder}`,
					},
				},
			},
			MuiButton: {
				defaultProps: {
					size: "small",
				},
				styleOverrides: {
					root: {
						textTransform: "none",
						boxShadow: "none",
					},
				},
			},
			MuiDivider: {
				styleOverrides: {
					root: {
						borderColor: color.ContentWrapperBorder,
					},
				},
			},
			MuiChip: {
				defaultProps: {
					variant: "outlined",
					color: "default",
				},
				styleOverrides: {
					root: {
						height: 26,
						borderRadius: "16px",
						fontSize: "12px",
						fontWeight: 500,
					},
					outlined: {
						backgroundColor: "#FAFAFA",
						borderColor: "#E9EAEB",
						color: "#414651",
					},
					filled: {
						backgroundColor: "#E9EAEB",
						borderColor: "#E9EAEB",
						color: "#414651",
					},
					colorPrimary: {
						"&.MuiChip-outlined": {
							color: "#821320",
							backgroundColor: "transparent",
							borderColor: "#EFA6AE",
						},
						"&.MuiChip-filled": {
							color: color.PrimaryText,
							backgroundColor: color.PrimaryMain,
							borderColor: color.PrimaryMain,
						},
					},
				},
			},
			MuiAccordion: {
				styleOverrides: {
					root: {
						boxShadow: "none",

						"&:before": { display: "none" },

						"&.Mui-expanded": {
							margin: 0,
						},
					},
				},
			},
			MuiTableBody: {
				styleOverrides: {
					root: {
						"& th.MuiTableCell-root": {
							color: "#181D27",
							fontWeight: 500,
						},
					},
				},
			},
			MuiTableCell: {
				styleOverrides: {
					head: {
						backgroundColor: "#FAFAFA",
						color: "#717680",
						fontSize: 12,
						fontWeight: 600,
						paddingTop: "12px",
						paddingBottom: "12px",
						borderBottom: "solid 1px #E9EAEB",
					},
					body: {
						fontSize: 14,
					},
				},
			},
			MuiTableRow: {
				styleOverrides: {
					root: {
						backgroundColor: "#FFFFFF",
						// borderBottom: "solid 1px #E9EAEB",
					},
				},
			},
			MuiDialogTitle: {
				styleOverrides: {
					root: {
						fontSize: 20,
						fontWeight: 600,
						paddingBottom: 0,
					},
				},
			},
			MuiDialogContentText: {
				styleOverrides: {
					root: {
						fontSize: 14,
						color: "#535862",
						marginBottom: 12,
						fontWeight: 400,
					},
				},
			},
			MuiMenu: {
				styleOverrides: {
					paper: {
						boxShadow: "var(--Paper-shadow) !important",
						paddingLeft: 0,
						paddingRight: 0,
					},
				},
			},
		},
	};

	const customVariables = {
		colors: {
			red: color.Red,
			border: color.Border,
			gray: color.Gray,
			validation: color.Validation,
			darkGrey: color.DarkGrey,
			placeholder: color.Placeholder,
			fieldBackground: color.FieldBackground,
			primaryMainTransparent: `${color.PrimaryMain}1f`,
			contentWrapperBorder: color.ContentWrapperBorder,
			radioBtnDisabled: color.RadioBtnDisabled,
			workflowIcon: color.WorkflowIcon,
		},
		shadow: {
			primary: "none",
			primaryHover: "0px 0px 15px 0px rgba(0, 0, 0, 0.15) inset",
		},
		borderRadius: 0,
		vh: (val: number) => {
			return `calc(var(--vh, 1vh) * ${val})`;
		},
	};

	return createTheme(Object.assign(theme, customVariables)) as Theme;
};

// TODO enum;
export const getTheme = () => {
	return theme(colors());
};
